import {
  ALERT_RESOLUTION_LABEL,
  ALERT_SEVERITY_LABEL,
  ALERT_STATUS_LABEL,
  APPLIANCE_STATUS_LABEL,
  TENANT_STATUS_LABEL,
  type AlertRecurrence,
  type AlertResolution,
  type AlertSeverity,
  type AlertStatus,
  type ApplianceHealthPoint,
  type ApplianceStatus,
  type TenantPlan,
  type TenantStatus,
} from '@minisoc/contracts';
import { cn } from '../cn.js';
import {
  ALERT_STATUS_TONE,
  APPLIANCE_STATUS_TONE,
  SEVERITY_TONE,
  TENANT_PLAN_TONE,
  TENANT_STATUS_TONE,
} from '../tokens.js';
import { Badge, Dot } from './primitives.js';

/**
 * Badges métier.
 *
 * Ces composants sont la seule façon autorisée d'afficher une sévérité ou un
 * statut : ils garantissent que le libellé français et la couleur restent
 * synchronisés avec les contrats partagés.
 */

export const SeverityBadge = ({ severity }: { severity: AlertSeverity }) => (
  <Badge tone={SEVERITY_TONE[severity].badge}>{ALERT_SEVERITY_LABEL[severity]}</Badge>
);

export const SeverityDot = ({ severity }: { severity: AlertSeverity }) => (
  <Dot className={SEVERITY_TONE[severity].solid} />
);

export const AlertStatusBadge = ({
  status,
  resolution,
}: {
  status: AlertStatus;
  resolution?: AlertResolution | null;
}) => {
  // Une alerte résolue en faux positif est un signal de bruit de détection :
  // on le rend visible plutôt que de l'agréger avec les vrais positifs.
  const label =
    status === 'RESOLVED' && resolution === 'FALSE_POSITIVE'
      ? ALERT_RESOLUTION_LABEL.FALSE_POSITIVE
      : ALERT_STATUS_LABEL[status];

  return <Badge tone={ALERT_STATUS_TONE[status].badge}>{label}</Badge>;
};

export const ApplianceStatusBadge = ({ status }: { status: ApplianceStatus }) => (
  <Badge tone={APPLIANCE_STATUS_TONE[status].badge}>{APPLIANCE_STATUS_LABEL[status]}</Badge>
);

export const PlanBadge = ({ plan }: { plan: TenantPlan }) => (
  <Badge tone={TENANT_PLAN_TONE[plan].badge}>{plan}</Badge>
);

export const TenantStatusBadge = ({ status }: { status: TenantStatus }) => (
  <Badge tone={TENANT_STATUS_TONE[status].badge}>{TENANT_STATUS_LABEL[status]}</Badge>
);

/**
 * Récurrence d'une alerte sur son poste.
 *
 * Une occurrence isolée reste volontairement discrète : c'est le cas courant, le
 * signaler visuellement diluerait l'attention. La mise en évidence commence à la
 * deuxième occurrence, seuil à partir duquel il y a une répétition à expliquer,
 * et passe en teinte d'alerte au-delà de cinq, où la détection ou le poste
 * demandent une action de fond plutôt qu'une nouvelle levée de doute.
 */
export function RecurrenceBadge({ recurrence }: { recurrence?: AlertRecurrence }) {
  if (!recurrence) return <span className="text-text-dim">—</span>;

  const { sameRule, endpointTotal, windowDays } = recurrence;
  const tone =
    sameRule >= 5
      ? 'bg-high/10 text-high ring-high/30'
      : sameRule >= 2
        ? 'bg-electric/10 text-electric ring-electric/30'
        : 'bg-surface-sunken text-text-muted ring-border-subtle';

  return (
    <span
      className="inline-flex items-baseline gap-1.5"
      title={`${sameRule} alerte(s) de cette règle et ${endpointTotal} au total sur cette machine, sur ${windowDays} jours glissants`}
    >
      <span
        className={cn(
          'inline-flex items-center rounded px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums ring-1',
          tone,
        )}
      >
        ×{sameRule}
      </span>
      <span className="font-mono text-[0.6875rem] text-text-dim tabular-nums">
        /{endpointTotal}
      </span>
    </span>
  );
}

const HEALTH_BAR_TONE: Record<ApplianceHealthPoint['status'], string> = {
  ok: 'bg-success',
  degraded: 'bg-high',
  down: 'bg-critical',
  unknown: 'bg-steel',
};

/**
 * Sparkline de disponibilité sur 7 jours (colonne « Santé 7j » du backoffice).
 *
 * Une barre par jour, hauteur proportionnelle à l'uptime, couleur par état. Un
 * jour sans donnée reste visible en gris plutôt que d'être omis : un trou dans
 * la supervision est une information en soi.
 */
export function HealthBars({ points }: { points: readonly ApplianceHealthPoint[] }) {
  return (
    <div className="flex h-6 items-end gap-1" role="img" aria-label="Disponibilité sur 7 jours">
      {points.map((point) => (
        <span
          key={point.date}
          title={`${point.date} — ${Math.round(point.uptime * 100)}%`}
          className={cn('w-1.5 rounded-sm', HEALTH_BAR_TONE[point.status])}
          style={{ height: `${Math.max(15, Math.round(point.uptime * 100))}%` }}
        />
      ))}
    </div>
  );
}
